/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ErrorSeverity } from 'src/store/types';
import styled from 'styled-components';
import { error, info } from 'src/shared/utils';
import { getErrorFromState } from '../../store/selectors/error';

// Sagas turn every exception into a toast. Axios errors are API responses (the backend reports its own
// failures); anything else is a frontend bug that would otherwise never reach Sentry. The empty Error of
// the initial state has no message.
const reportUnexpectedError = (originalError?: Error) => {
  if (originalError?.message && !(originalError as AxiosError).isAxiosError) {
    Sentry.captureException(originalError);
  }
};

const ErrorList = styled.ul`
  padding-left: 20px;
  margin-top: 12px;
`;

const ListItem = styled.li`
  text-align: left;
`;

export const GlobalError: FC = () => {
  const globalErrorState = useSelector(getErrorFromState);

  const isProduction =
    process.env.REACT_APP_ENV && process.env.REACT_APP_ENV === 'production';

  const logError = () =>
    isProduction &&
    console.info(
      `[${globalErrorState.severity}]: ${globalErrorState.originalError}`,
    );

  useEffect(() => {
    if (globalErrorState.originalError) {
      logError();
      reportUnexpectedError(globalErrorState.originalError);
    }

    if (globalErrorState.message && globalErrorState.message !== '') {
      let messageToShow: string | React.ReactNode = globalErrorState.message;

      if (Array.isArray(globalErrorState.message)) {
        messageToShow = (
          <ErrorList>
            {globalErrorState.message.map((message, idx) => (
              <ListItem key={idx}>{message}</ListItem>
            ))}
          </ErrorList>
        );
      }

      switch (globalErrorState.severity) {
        case ErrorSeverity['WARNING']: {
          void info(messageToShow as string);
          if (!isProduction) {
            console.warn(globalErrorState.originalError);
          }
          break;
        }

        case ErrorSeverity['INFO']: {
          if (!isProduction) {
            console.info(messageToShow);
          }
          break;
        }

        default: {
          void error(messageToShow as string);
          if (!isProduction) {
            console.error(globalErrorState.originalError);
          }
          break;
        }
      }
    }
  }, [globalErrorState]);

  return null;
};
